/**
 * Waveform peaks for the Audio node: decode a clip once and reduce it to a few
 * hundred amplitude buckets the canvas can draw.
 */

export const WAVEFORM_BUCKETS = 240;

/**
 * One peak per bucket from raw channel samples, normalised so the loudest bucket
 * is 1. Pure, so it can be tested without an AudioContext.
 */
export const peaksFromSamples = (channels: Float32Array[], buckets = WAVEFORM_BUCKETS): Float32Array => {
    const out = new Float32Array(buckets);
    const length = channels[0]?.length ?? 0;
    if (!length || !channels.length || buckets <= 0) return out;
    const perBucket = length / buckets;
    let loudest = 0;
    for (let b = 0; b < buckets; b++) {
        const start = Math.floor(b * perBucket);
        const end = Math.min(length, Math.max(start + 1, Math.floor((b + 1) * perBucket)));
        let peak = 0;
        for (const channel of channels) {
            for (let i = start; i < end; i++) {
                const v = Math.abs(channel[i]);
                if (v > peak) peak = v;
            }
        }
        out[b] = peak;
        if (peak > loudest) loudest = peak;
    }
    if (loudest > 0) for (let b = 0; b < buckets; b++) out[b] /= loudest;
    return out;
};

const MAX_CACHED = 8;
const cache = new Map<string, Promise<Float32Array>>();

/**
 * Decode the audio at `url` (data, blob or https) and return its peaks. Results
 * are cached per URL so re-renders and reconnects do not decode again.
 */
export const loadWaveform = (url: string, buckets = WAVEFORM_BUCKETS): Promise<Float32Array> => {
    const key = `${buckets}|${url}`;
    const pending = cache.get(key);
    if (pending) return pending;

    const task = (async () => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const bytes = await response.arrayBuffer();
        const Offline = (window as Window & { OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext })
            .OfflineAudioContext
            || (window as Window & { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
        if (!Offline) throw new Error('Web Audio is not available');
        // An offline context decodes without touching audio output or autoplay rules.
        const decoded = await new Offline(1, 1, 44100).decodeAudioData(bytes);
        const channels = Array.from({ length: decoded.numberOfChannels }, (_, i) => decoded.getChannelData(i));
        return peaksFromSamples(channels, buckets);
    })();

    cache.set(key, task);
    if (cache.size > MAX_CACHED) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
    }
    task.catch(() => cache.delete(key));
    return task;
};
